/**
 * cart-upgrades.js
 * UPGRADES inline dans le cart drawer.
 * Affiche une carte sous la ligne du produit à remplacer.
 * Séparé de cart-upsells.js pour plus de clarté.
 *
 * Structure liquid requise dans cart-items.liquid (déjà en place) :
 *   <tr data-upsell-slot data-upsell-parent-variant="{{ item.variant.id }}">
 *     <td colspan="2">
 *       <div class="cart-item-upsell-placeholder"></div>
 *     </td>
 *   </tr>
 */
(function () {
  "use strict";

  const SEL_SLOT = "[data-upsell-slot]";

  let _debounce = null;
  let _runId    = 0;

  // ─── UTILITAIRES ─────────────────────────────────────────────

  function scheduleRefresh(ms) {
    clearTimeout(_debounce);
    _debounce = setTimeout(refresh, ms ?? 80);
  }

  async function fetchCart() {
    const r = await fetch("/cart.js", { headers: { Accept: "application/json" } });
    if (!r.ok) throw new Error("cart.js " + r.status);
    return r.json();
  }

  // JSON format — même approche que le bouton S'abonner upsell (prouvé fonctionnel)
  async function cartAdd(variantId, sellingPlanId) {
    const payload = {
      id: Number(variantId),
      quantity: 1,
      sections: ["cart-drawer", "cart-icon-bubble"],
      sections_url: window.location.pathname,
    };
    if (sellingPlanId) payload.selling_plan = Number(sellingPlanId);
    const r = await fetch("/cart/add.js", {
      method: "POST",
      credentials: "same-origin",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    });
    if (!r.ok) {
      const d = await r.json().catch(() => ({}));
      throw new Error(d.description || d.message || "Ajout impossible");
    }
    return r.json();
  }

  async function cartRemove(variantIds) {
    if (!variantIds.length) return;
    const updates = {};
    variantIds.forEach(id => { updates[String(id)] = 0; });
    await fetch("/cart/update.js", {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({ updates }),
    });
  }


  async function fetchCard(handle, variantId, isSubscription) {
    const sub = isSubscription ? "&subscription=1" : "";
    const r = await fetch(`/products/${handle}?view=upgrade-card&variant=${variantId}${sub}`);
    if (!r.ok) throw new Error("card 404: " + handle);
    const text = await r.text();
    const tmp = document.createElement("div");
    tmp.innerHTML = text.trim();
    const card = tmp.querySelector("[data-upsell-card]");
    return card ? text : null;
  }

  // ─── RÈGLES ──────────────────────────────────────────────────

  function csv(val) {
    return String(val || "").split(",").map(s => s.trim()).filter(Boolean);
  }

  function getRules() {
    return (window.MOODZ_UPGRADE_RULES || [])
      .filter(r => r.upgradeHandle && r.upgradeVariantId && r.replaceVariantIds)
      .map(r => ({
        handle:      String(r.upgradeHandle).trim(),
        variantId:   String(r.upgradeVariantId).trim(),
        replaceIds:  csv(r.replaceVariantIds),
        excludeIds:  csv(r.excludeVariantIds),
        minQty:      Number(r.minQty) || 1,
        // styles
        cardBg:      r.cardBg             || "",
        cardRadius:  Number(r.cardRadius) || 0,
        borderW:     Number(r.cardBorderWidth) || 0,
        borderC:     r.cardBorderColor    || "",
        imageUrl:    r.imageOverride      || "",
        customTitle: r.customTitle        || "",
        priceText:   r.customPriceMain    || "",
        compareText: r.customPriceCompare || "",
        priceColor:  r.priceMainColor     || "",
        compareColor:r.priceCompareColor  || "",
        btnLabel:    (function(){ const raw = String(r.buttonLabel || ""); const LEGACY = "Passer à l'upgrade"; return raw && !raw.startsWith(LEGACY) ? raw : ""; })(),
        btnBg:       r.buttonBg           || "",
        btnColor:    r.buttonTextColor    || "",
        btnRadius:   Number(r.buttonRadius)      || 0,
        btnBorderW:  Number(r.buttonBorderWidth) || 0,
        btnBorderC:  r.buttonBorderColor         || "",
      }));
  }

  // ─── FILTRE ───────────────────────────────────────────────────

  function filterRules(rules, cartItems) {
    const qty = {};
    cartItems.forEach(item => {
      qty[String(item.id)] = (qty[String(item.id)] || 0) + item.quantity;
    });
    const inCart = id => (qty[String(id)] || 0) > 0;

    return rules.filter(rule => {
      // L'upgrade s'affiche si AU MOINS UN replaceId est dans le panier
      if (!rule.replaceIds.some(id => inCart(id))) return false;
      // Quantité minimale requise
      if (rule.minQty > 1 && !rule.replaceIds.some(id => (qty[String(id)] || 0) >= rule.minQty)) return false;
      // Masquer si l'upgrade est déjà dans le panier
      if (inCart(rule.variantId)) return false;
      // Excludes
      if (rule.excludeIds.some(id => inCart(id))) return false;
      return true;
    });
  }

  // ─── STYLES ───────────────────────────────────────────────────

  function applyStyle(cardEl, rule) {
    if (!cardEl) return;
    if (rule.cardBg)     cardEl.style.backgroundColor = rule.cardBg;
    if (rule.cardRadius) cardEl.style.borderRadius     = rule.cardRadius + "px";
    cardEl.style.borderWidth = (rule.borderW || 0) + "px";
    cardEl.style.borderStyle = rule.borderW > 0 ? "solid" : "";
    if (rule.borderC)    cardEl.style.borderColor = rule.borderC;

    const img = cardEl.querySelector("[data-upsell-image-wrapper] img");
    if (img && rule.imageUrl) img.src = rule.imageUrl;

    const title = cardEl.querySelector("[data-upsell-title]");
    if (title && rule.customTitle) title.textContent = rule.customTitle;

    const price = cardEl.querySelector("[data-upsell-price-main]");
    if (price) {
      if (rule.priceText) price.textContent = rule.priceText.replace("{price}", price.textContent.trim());
      if (rule.priceColor) price.style.setProperty("color", rule.priceColor, "important");
    }

    const compare = cardEl.querySelector("[data-upsell-price-compare]");
    if (compare) {
      if (rule.compareText) {
        compare.textContent = rule.compareText.replace("{compare_price}", compare.textContent.trim());
        compare.style.removeProperty("display");
      }
      if (rule.compareColor) compare.style.setProperty("color", rule.compareColor, "important");
    }

    const btn = cardEl.querySelector("[data-upsell-button]");
    if (btn) {
      const ctalabel = cardEl.querySelector('[data-upsell-cta-label]');
      if (ctalabel && rule.btnLabel) ctalabel.textContent = rule.btnLabel;
      if (rule.btnBg)     btn.style.background   = rule.btnBg;
      if (rule.btnColor)  btn.style.color        = rule.btnColor;
      if (rule.btnRadius) btn.style.borderRadius = rule.btnRadius + "px";
      btn.style.borderWidth = (rule.btnBorderW || 0) + "px";
      btn.style.borderStyle = rule.btnBorderW > 0 ? "solid" : "";
      if (rule.btnBorderC) btn.style.borderColor = rule.btnBorderC;
    }
  }

  // ─── HELPERS SUBSCRIPTION PRICE ─────────────────────────────

  function formatMoneyFromCents(cents) {
    const fmt = (window.Shopify && window.Shopify.money_format) || "";
    const amount = (cents / 100).toFixed(2);
    if (fmt) {
      return fmt
        .replace("{{amount}}", amount)
        .replace("{{amount_with_comma_separator}}", amount.replace(".", ","))
        .replace("{{amount_no_decimals}}", Math.round(cents / 100))
        .replace("{{amount_no_decimals_with_comma_separator}}", Math.round(cents / 100));
    }
    return amount.replace(".", ",") + " €";
  }

  function updateUpgradeSubPrice(cardEl, variantId, productJson) {
    if (!cardEl || !productJson) return;
    const variant = (productJson.variants || []).find(v => String(v.id) === String(variantId));
    if (!variant) return;
    const alloc    = (variant.selling_plan_allocations || [])[0];
    const regPrice = variant.price;

    let subPrice = alloc && alloc.price != null ? alloc.price : null;

    // Fallback : calculer depuis price_adjustments si alloc.price est nil
    if (subPrice == null) {
      const spGroup = (productJson.selling_plan_groups || [])[0];
      const spPlan  = spGroup ? (spGroup.selling_plans || [])[0] : null;
      const adj     = spPlan ? (spPlan.price_adjustments || [])[0] : null;
      if (adj) {
        if (adj.value_type === "percentage") {
          subPrice = Math.round(regPrice * (1 - adj.value / 100));
        } else if (adj.value_type === "price") {
          subPrice = adj.value;
        } else if (adj.value_type === "fixed_amount") {
          subPrice = regPrice - adj.value;
        }
      }
    }

    if (subPrice == null) return;
    const savings = regPrice - subPrice;

    // Patch CTA — toujours patcher le handle → permet le fallback ATC même si alloc est null
    const ctaBtn = cardEl.querySelector("[data-upsell-add-to-cart]");
    if (ctaBtn) {
      ctaBtn.setAttribute("data-product-handle", productJson.handle || "");
      // Patcher selling-plan-id seulement si variant-specific est disponible
      const variantSellingPlanId = alloc && alloc.selling_plan_id
        ? alloc.selling_plan_id
        : null;
      if (variantSellingPlanId) {
        ctaBtn.setAttribute("data-selling-plan-id", String(variantSellingPlanId));
      }
    }

    const priceMain = cardEl.querySelector("[data-upsell-price-main]");
    if (priceMain) priceMain.textContent = formatMoneyFromCents(subPrice);

    // Supprimer l'<s> existant (potentiellement invisible ou mal sérialisé sur nœud détaché)
    const oldCompare = cardEl.querySelector("[data-upsell-price-compare]");
    if (oldCompare) oldCompare.remove();

    // Créer un nouvel <s> avec styles inline garantis — même pattern que le savings span
    if (regPrice > subPrice) {
      const priceWrapper = cardEl.querySelector(".moodz-upgrade-card__price");
      if (priceWrapper) {
        const newCompare = document.createElement("s");
        newCompare.setAttribute("data-upsell-price-compare", "");
        newCompare.style.cssText = "font-size:11px;opacity:0.6;text-decoration:line-through;margin-right:3px;";
        newCompare.textContent = formatMoneyFromCents(regPrice);
        priceWrapper.insertBefore(newCompare, priceWrapper.firstChild);
      }
    }

    if (savings > 0) {
      let savingsEl = cardEl.querySelector(".moodz-upgrade-card__cta-savings");
      if (!savingsEl) {
        const labelEl = cardEl.querySelector(".moodz-upgrade-card__cta-label");
        if (labelEl) {
          savingsEl = document.createElement("span");
          savingsEl.className = "moodz-upgrade-card__cta-savings";
          labelEl.after(savingsEl);
        }
      }
      if (savingsEl) savingsEl.textContent = "Économisez " + formatMoneyFromCents(savings);
    }
  }

  // ─── REFRESH ─────────────────────────────────────────────────

  async function refresh() {
    const slots = Array.from(document.querySelectorAll(SEL_SLOT));

    // Vider tous les slots d'abord
    slots.forEach(slot => {
      const ph = slot.querySelector(".cart-item-upsell-placeholder");
      if (ph) ph.innerHTML = "";
      slot.hidden = true;
    });

    if (!slots.length) return;

    const runId = ++_runId;

    try {
      const cart   = await fetchCart();
      if (runId !== _runId) return;

      const rules  = getRules();
      const active = filterRules(rules, cart.items || []);

      if (!active.length) return;

      // Détecte quels replaceIds sont en abonnement dans le panier
      const cartSubIds = new Set(
        (cart.items || [])
          .filter(item => item.selling_plan_allocation != null)
          .map(item => String(item.id))
      );
      active.forEach(rule => {
        rule.isSubscription = rule.replaceIds.some(id => cartSubIds.has(id));
      });

      // Fetch les cartes en parallèle
      const htmlParts = await Promise.all(
        active.map(r =>
          Promise.all([
            fetchCard(r.handle, r.variantId, r.isSubscription).catch(() => null),
            r.isSubscription
              ? fetch(`/products/${r.handle}.js`).then(res => res.json()).catch(() => null)
              : Promise.resolve(null),
          ])
        )
      );
      if (runId !== _runId) return;

      // Règle la plus spécifique (minQty élevé) en premier — évite qu'une règle large écrase une règle précise
      active.sort((a, b) => b.minQty - a.minQty);

      active.forEach((rule, i) => {
        const [html, productJson] = htmlParts[i] || [];
        if (!html) return;

        // Trouver le slot dont data-upsell-parent-variant matche un des replaceIds
        const targetSlot = slots.find(slot =>
          rule.replaceIds.includes(slot.dataset.upsellParentVariant)
        );
        if (!targetSlot) return;

        const placeholder = targetSlot.querySelector(".cart-item-upsell-placeholder");
        if (!placeholder) return;

        // Slot déjà pris par une règle plus spécifique — ne pas écraser
        if (placeholder.innerHTML.trim()) return;

        const tmp = document.createElement("div");
        tmp.innerHTML = html;
        applyStyle(tmp.querySelector("[data-upsell-card]"), rule);
        if (rule.isSubscription && productJson) {
          updateUpgradeSubPrice(tmp.querySelector("[data-upsell-card]"), rule.variantId, productJson);
        }

        placeholder.innerHTML = `
          <div class="cart-item-upsell-inline">
            <div class="moodz-upgrade-connector" aria-hidden="true">
              <div class="moodz-upgrade-connector__line"></div>
              <div class="moodz-upgrade-connector__pill">
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M7 16V4m0 0L3 8m4-4l4 4"/><path d="M17 8v12m0 0l4-4m-4 4l-4-4"/></svg>
                Remplace votre sélection actuelle
              </div>
              <div class="moodz-upgrade-connector__line"></div>
            </div>
            ${tmp.innerHTML}
          </div>`;
        targetSlot.hidden = false;
      });

    } catch (err) {
      console.error("[UPSELL UPGRADE] refresh:", err);
    }
  }

  // ─── ATC (capture phase — premier handler, bloque tous les autres) ──────────
  // Écoute sur [data-upsell-button] exclusif à product-upgrade-card.liquid.
  // stopImmediatePropagation() empêche cart-upsells.js (bubble) de s'exécuter.

  document.addEventListener("click", async function (e) {
    const btn = e.target.closest("[data-upsell-button]");
    if (!btn) return;
    if (btn.disabled) return;

    // Capture totale : aucun autre handler ne reçoit cet événement
    e.stopImmediatePropagation();

    const variantId  = btn.dataset.variantId;
    const rawPlanId  = btn.dataset.sellingPlanId ? Number(btn.dataset.sellingPlanId) : null;
    if (!variantId) return;

    const rules = getRules();
    const rule  = rules.find(r => r.variantId === String(variantId));
    if (!rule) return;

    btn.disabled = true;
    btn.classList.add("is-loading");

    try {
      // ── ÉTAPE 1 : Identifier les lignes parent à supprimer ────────────────
      const ROW_SEL = '[id^="CartDrawer-Item-"].cart-item--normal';
      const QTY_SEL = 'input.quantity__input[name="updates[]"][data-index]';
      const parentLines = [];

      for (const row of document.querySelectorAll(ROW_SEL)) {
        const inp = row.querySelector(QTY_SEL);
        if (!inp) continue;
        if (!rule.replaceIds.includes(String(inp.dataset.quantityVariantId || ""))) continue;
        const line = Number(inp.dataset.index);
        if (line) parentLines.push(line);
      }

      // ── ÉTAPE 2 : Supprimer le parent (du bas vers le haut) ──────────────
      for (const line of [...parentLines].sort((a, b) => b - a)) {
        await fetch("/cart/change.js", {
          method: "POST",
          headers: { "Content-Type": "application/json", Accept: "application/json" },
          credentials: "same-origin",
          body: JSON.stringify({ line, quantity: 0 }),
        });
      }
      if (!parentLines.length) await cartRemove(rule.replaceIds);

      // ── ÉTAPE 3 : Ajouter l'upgrade ──────────────────────────────────────
      // rawPlanId = data-selling-plan-id injecté par Liquid (sp.id).
      // Null si le parent était one-time (is_sub=0) → ajout one-time.
      const parsedState = await cartAdd(variantId, rawPlanId);

      // ── ÉTAPE 4 : Re-render via renderContents natif Pitch ───────────────
      // parsedState.sections["cart-drawer"] contient le HTML frais retourné
      // directement par /cart/add.js — même chemin que product-form.js.
      const cartDrawerEl = document.querySelector("cart-drawer");
      if (cartDrawerEl && parsedState.sections) {
        cartDrawerEl.renderContents(parsedState);
      }
      requestAnimationFrame(() => scheduleRefresh(150));
      if (typeof window.MOODZ_UPSELL_REFRESH === "function") {
        setTimeout(window.MOODZ_UPSELL_REFRESH, 300);
      }

    } catch (err) {
      console.error("[UPSELL UPGRADE] ATC:", err);
      const card = btn.closest("[data-upsell-card]");
      let msg = card?.querySelector("[data-upsell-msg]");
      if (!msg && card) {
        msg = document.createElement("p");
        msg.dataset.upsellMsg = "";
        msg.style.cssText = "color:red;font-size:11px;margin:4px 0 0;text-align:center";
        card.appendChild(msg);
      }
      if (msg) {
        msg.textContent = err.message || "Erreur lors de l'ajout";
        setTimeout(() => { msg.textContent = ""; }, 4000);
      }
    } finally {
      btn.disabled = false;
      btn.classList.remove("is-loading");
    }
  }, true); // ← capture phase

  // ─── LISTENERS ───────────────────────────────────────────────

  function listenPubSub() {
    if (typeof subscribe === "function" && window.PUB_SUB_EVENTS?.cartUpdate) {
      subscribe(PUB_SUB_EVENTS.cartUpdate, () => scheduleRefresh(120));
    } else {
      setTimeout(listenPubSub, 300);
    }
  }

  // MutationObserver : déclenche refresh dès qu'un [data-upsell-slot]
  // apparaît dans le DOM (après tout re-render du cart drawer)
  function watchNewSlots() {
    let _watchDebounce = null;
    const obs = new MutationObserver(function(mutations) {
      const hasNewSlot = mutations.some(function(m) {
        return Array.from(m.addedNodes).some(function(n) {
          if (n.nodeType !== 1) return false;
          return (typeof n.matches === 'function' && n.matches('[data-upsell-slot]'))
            || (typeof n.querySelector === 'function' && n.querySelector('[data-upsell-slot]'));
        });
      });
      if (!hasNewSlot) return;
      clearTimeout(_watchDebounce);
      _watchDebounce = setTimeout(function() { scheduleRefresh(0); }, 60);
    });
    obs.observe(document.body, { childList: true, subtree: true });
  }

  // ─── INIT ─────────────────────────────────────────────────────

  window.MOODZ_UPGRADE_REFRESH = () => scheduleRefresh(0);

  document.addEventListener("DOMContentLoaded", () => {
    watchNewSlots();
    listenPubSub();
    scheduleRefresh(0);
  });

  document.addEventListener("shopify:section:load", () => scheduleRefresh(0));

})();